import { X } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { DiscordIcon } from '@/Domain/components/icons/Discord.icon';

export const DISCORD_GUILD_CONFLICT_TOAST_ID = 'discord-guild-conflict';
const TITLE_ID = 'discord-guild-conflict-title';
const CHOICE_LEAD = <strong className="font-semibold text-foreground" />;

export interface DiscordGuildConflictToastOptions {
  title: string;
  onMove: () => void;
  onOpenAdvanced?: () => void;
}

export const DiscordGuildConflictNotice = ({
  title,
  onMove,
  onOpenAdvanced,
}: DiscordGuildConflictToastOptions) => {
  const { t } = useTranslation(['integration']);
  const dismiss = () => toast.dismiss(DISCORD_GUILD_CONFLICT_TOAST_ID);

  return (
    <div
      role="group"
      aria-labelledby={TITLE_ID}
      data-testid="discord-guild-conflict-notice"
      className="pointer-events-auto w-[22rem] max-w-[calc(100vw-2rem)] rounded-xl border border-border bg-card p-4 shadow-lg"
    >
      <div className="flex gap-3">
        <span
          aria-hidden="true"
          className="grid size-8 shrink-0 place-items-center rounded-lg bg-info-muted text-info"
        >
          <DiscordIcon className="size-4" style={{ fill: 'currentColor' }} />
        </span>
        <div className="min-w-0 flex-1 break-words text-sm">
          <p id={TITLE_ID} className="font-medium text-foreground">
            {title}
          </p>
          <p className="mt-1 text-muted-foreground">
            {t(
              'integration:discord.oauth_callback.failure.guild_already_connected_move_description',
            )}
          </p>
          <ul className="mt-2 space-y-1.5 text-muted-foreground">
            <li data-testid="discord-guild-conflict-move-choice">
              <Trans
                t={t}
                i18nKey="integration:discord.oauth_callback.failure.guild_already_connected_move_option"
                components={[CHOICE_LEAD]}
              />
            </li>
            {onOpenAdvanced ? (
              <li data-testid="discord-guild-conflict-advanced-choice">
                <Trans
                  t={t}
                  i18nKey="integration:discord.oauth_callback.failure.guild_already_connected_keep_both_option"
                  components={[CHOICE_LEAD]}
                />
              </li>
            ) : null}
          </ul>
        </div>
        <IconButton
          variant="ghost"
          size="sm"
          aria-label={t(
            'integration:discord.oauth_callback.failure.guild_already_connected_dismiss',
          )}
          onClick={dismiss}
          className="-mr-2 -mt-2 shrink-0"
          data-testid="discord-guild-conflict-dismiss"
        >
          <X className="size-4" aria-hidden="true" />
        </IconButton>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
        {onOpenAdvanced ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              dismiss();
              onOpenAdvanced();
            }}
            data-testid="discord-guild-conflict-advanced"
          >
            {t('integration:discord.oauth_callback.failure.guild_already_connected_action')}
          </Button>
        ) : null}
        <Button
          variant="dark"
          size="sm"
          onClick={() => {
            dismiss();
            onMove();
          }}
          data-testid="discord-guild-conflict-move"
        >
          {t('integration:discord.oauth_callback.failure.guild_already_connected_move_action')}
        </Button>
      </div>
    </div>
  );
};

// Custom + unstyled: sonner's own layout puts two actions inline and collapses the description.
export const showDiscordGuildConflictToast = (options: DiscordGuildConflictToastOptions): void => {
  toast.custom(() => <DiscordGuildConflictNotice {...options} />, {
    id: DISCORD_GUILD_CONFLICT_TOAST_ID,
    duration: Infinity,
    unstyled: true,
  });
};
